import React from 'react';
import { assoc, assocPath, evolve, map, prop } from 'ramda';
import { withRouter } from 'react-router-dom';

import http from 'api/http';
import { manufacturerAPI, boatAPI, trailerAPI } from 'api/requests';

import {
  debounce,
  getIsBoatManufacturer,
  getIsComponentManufacturer,
  getIsTrailerManufacturer,
  getIsOilManufacturer,
  getIsOilBrander,
} from 'helpers';

import WithUser from 'HOC/User';
import { ROLES } from 'constants/roles';

import Button, { buttonType, buttonColor } from 'Components/Button';
import Modal from 'Components/Modal';
import NotificationManager from 'dom-managers/notification';
import Slider from 'Components/Slider';
import Notes from 'Components/Notes';
import SummaryHeader from './Header';
import SummaryCertification from './Certification';
import SummaryGeneralInfo from './GeneralInfo';
import SummaryCheckList from './CheckList';
import BrandDetailsModal from './Brand';
import BrandsList from './BrandsList'
import Contacts from './Contacts';

const CLICKABLE_LIST_ITEM_DOCUMENT_TYPE = {
  AGREEMENT: 'Certification Agreement',
  MML: 'MML',
  INSPECTION_REPORT: 'Inspection Report'
};

@WithUser
@withRouter
export default class SummaryScene extends React.Component {
  constructor(props) {
    super(props);

    this.modalHeader = '';
    this.modalBody = '';

    this.modalConfirmAction = () => {};
    this.requestedToUpdateFieldsIds = [];

    this.isInternalUser = props.user.isInternal();

    this.state = {
      isModalOpen: false,
      currentYearInfo: '',
      isPWCAgreement: false,
      currentYear: '',
      year: '',
      brandsList: [],
      brandDetails: { errors: {} },
      yearInfoFetchFailed: false,
    };
  }

  roleSwitch = () => {
    const { user, match } = this.props;

    let URLRole = '';

    Object.keys(ROLES).forEach(roleKey => {
      if (match.params.manufacturerRole === ROLES[roleKey].nameForUrl) URLRole = ROLES[roleKey];
    });

    if (URLRole === ROLES.OIL_MANUFACTURER && !user.isOilManufacturer()) URLRole = ROLES.OIL_BRANDER;

    if (URLRole && user.getActiveRole() !== URLRole.serverName) {
      user.changeMenu(URLRole.serverName);
    }
  };

  componentDidMount() {
    const { updateManufacturer, user } = this.props;

    const manufacturerCode = user.getManufacturerCode();

    this.roleSwitch();

    http.get(manufacturerAPI.details(manufacturerCode))
      .then(({ data }) => updateManufacturer(data))
      .catch(() => this.setState({ yearInfoFetchFailed: true }));

    if (getIsBoatManufacturer(user)) {
      http.get(manufacturerAPI.addBrand).then(({ data: brandsList }) => {
        this.setState({ brandsList });
      });
    }

    this.getCurrentYear()
      .then(year => {
        this.setState({ currentYear: year }, () => {
          if (user.getSelectedYear()) {
            this.handleYearSelect(user.getSelectedYear());
          } else {
            this.handleYearSelect(year);
          }
        });
      })
      .catch(() => {
        this.setState({ yearInfoFetchFailed: true });
      });
  }

  getCurrentYear = () => {
    const { user } = this.props;

    this.modelYearReminder = '(June 1 - May 31)';
    this.yearSelectHeader = 'Model year';

    return new Promise((resolve, reject) => {
      switch (user.getActiveRole()) {
        case ROLES.BOAT_MANUFACTURER.serverName:
          http
            .get(boatAPI.currentModelYear)
            .then(({ data }) => resolve(data[1] || data[0]))
            .catch(() => {
              reject();
            });
          break;
        case ROLES.COMPONENT_MANUFACTURER.serverName:
          http
            .get(boatAPI.currentModelYear)
            .then(({ data }) => resolve(data[1] || data[0]))
            .catch(() => {
              reject();
            });
          break;
        case ROLES.TRAILER_MANUFACTURER.serverName:
          http
            .get(trailerAPI.currentModelYear)
            .then(({ data }) => resolve(data))
            .catch(() => {
              reject();
            });
          break;
        default:
          resolve(new Date().getFullYear());
      }
    });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.manufacturerRole !== this.props.match.params.manufacturerRole) {
      this.roleSwitch();
      this.handleYearSelect(this.state.year);
    }
  }

  _showAnnualCertificationAgreement = () => {
    const { user } = this.props;

    return (
      (getIsBoatManufacturer(user) || getIsTrailerManufacturer(user) || getIsComponentManufacturer(user)) &&
      !user.isInspector()
    );
  };

  handleRoleChange = ({ value: role }) => {
    const { user } = this.props;

    user.setActiveRole(role);
    this.getChecklistInfo({ role });
  };

  handleYearSelect = year => {
    const { user } = this.props;

    user.saveSelectedYear(year);
    this.setState({ year }, () => {
      this.getChecklistInfo({ year });
    });
  };

  getChecklistInfo = ({ role, year }) => {
    const { user } = this.props;
    let { currentYearInfo } = this.state;

    if (!role) role = user.getActiveRole();
    if (!year) year = currentYearInfo.year;

    this.setState({ currentYearInfo: '' }, () => {
      http
        .get(manufacturerAPI.checkList(role, year))
        .then(({ data }) => {
          this.setState({currentYearInfo: data, year }, this.promptCertificationStatusChange);
        })
        .catch(() => this.setState({ yearInfoFetchFailed: true }));
    });
  };

  handleNotesChange = debounce(value =>
    this.setState(assocPath(['currentYearInfo', 'notes'], value), this.saveChecklistInfo)
  );

  saveChecklistInfo = () => {
    const { user } = this.props;
    const { currentYearInfo } = this.state;
    const role = user.getActiveRole();
    const year = currentYearInfo.year;

    let itemValueMap = {};
    currentYearInfo.itemValues.forEach(
      (checklistItem) => {
        itemValueMap[checklistItem.id] = {
          isApplicable: checklistItem.isApplicable,
          autoSet: checklistItem.autoSet,
          isChecked: checklistItem.isChecked,
          date: checklistItem.date,
          id: checklistItem.id,
        };
      });
    let dataPayload = {
      id: currentYearInfo.id,
      notes: currentYearInfo.notes,
      isCertified: currentYearInfo.isCertified,
      itemValueMap,
    };

    return http.patch(manufacturerAPI.checkList(role, year), dataPayload);
  };

  closeModal = () => {
    this.setState({ isModalOpen: false, modalRequestInProcess: false });
  };

  changeManufacturerStatus = () => {
    const { user, updateManufacturer } = this.props;

    const _toggleManufacturerStatus = () => {
      this.setState({ modalRequestInProcess: true });
      http
        .put(manufacturerAPI.changeStatus(user.getManufacturerCode(), !user.getManufacturerStatus()))
        .then(() => {
          updateManufacturer({ active: !user.getManufacturerStatus() });
          NotificationManager.success(1, 'Manufacturer status successfully changed');
        })
        .finally(this.closeModal);
    };

    this.modalHeader = 'Confirmation';
    this.modalBody = `Are you sure you want to ${
      user.getManufacturerStatus() ? 'Deactivate' : 'Activate'
    } ${user.getManufacturerName()}?`;
    this.modalConfirmAction = _toggleManufacturerStatus;
    this.setState({ isModalOpen: true });
  };

  onCheckListStatusChange = id => {
    if (this.isInternalUser && this.requestedToUpdateFieldsIds.indexOf(id) === -1) {
      this.requestedToUpdateFieldsIds.push(id);
      let { currentYearInfo } = this.state;

      let dataEntryToChange = currentYearInfo.itemValues.find(entry => entry.id === id);
      dataEntryToChange.isChecked = !dataEntryToChange.isChecked;
      dataEntryToChange.date = new Date();
      this.setState({ currentYearInfo });

      this.saveChecklistInfo()
        .then(() => {
          this.promptCertificationStatusChange(!dataEntryToChange.isChecked);
        })
        .catch(() => {
          let dataEntryToChange = currentYearInfo.itemValues.find(entry => entry.id === id);
          dataEntryToChange.isChecked = !dataEntryToChange.isChecked;
          dataEntryToChange.date = new Date();
          this.setState({ currentYearInfo });
        })
        .finally(() => {
          const indexOfIdInRequestsList = this.requestedToUpdateFieldsIds.indexOf(id);
          if (indexOfIdInRequestsList !== -1) this.requestedToUpdateFieldsIds.splice(indexOfIdInRequestsList, 1);
        });
    }
  };

  onCheckListApplicableChange = ({ id, prevValue }) => {
    const toggleApplicable = evolve({
      currentYearInfo: evolve({
        itemValues: map(listItem =>
          listItem.id === id ? assoc('isApplicable', !listItem.isApplicable, listItem) : listItem
        ),
      }),
    });

    this.setState(
      toggleApplicable,
      () => this.saveChecklistInfo()
        .then(() => this.promptCertificationStatusChange(prevValue))
        .catch(() => this.setState(toggleApplicable))
    );
  };

  onCheckListAutomationChange = id => {
    if (this.isInternalUser && this.requestedToUpdateFieldsIds.indexOf(id) === -1) {
      this.requestedToUpdateFieldsIds.push(id);
      let { currentYearInfo } = this.state;

      let dataEntryToChange = currentYearInfo.itemValues.find(entry => entry.id === id);
      dataEntryToChange.autoSet = !dataEntryToChange.autoSet;
      this.setState({ currentYearInfo });

      this.saveChecklistInfo()
        .catch(() => {
          let dataEntryToChange = currentYearInfo.itemValues.find(entry => entry.id === id);
          dataEntryToChange.autoSet = !dataEntryToChange.autoSet;
          this.setState({ currentYearInfo });
        })
        .finally(() => {
          const indexOfIdInRequestsList = this.requestedToUpdateFieldsIds.indexOf(id);
          if (indexOfIdInRequestsList !== -1) this.requestedToUpdateFieldsIds.splice(indexOfIdInRequestsList, 1);
        });
    }
  };

  onCheckListDateChange = id => newDate => {
    if (this.isInternalUser && this.requestedToUpdateFieldsIds.indexOf(id) === -1) {
      this.requestedToUpdateFieldsIds.push(id);
      let { currentYearInfo } = this.state;

      let dataEntryToChange = currentYearInfo.itemValues.find(entry => entry.id === id);
      const oldDate = dataEntryToChange.date;
      dataEntryToChange.date = newDate;
      this.setState({ currentYearInfo });

      this.saveChecklistInfo()
        .catch(() => {
          let dataEntryToChange = currentYearInfo.itemValues.find(entry => entry.id === id);
          dataEntryToChange.date = oldDate;
          this.setState({ currentYearInfo });
        })
        .finally(() => {
          const indexOfIdInRequestsList = this.requestedToUpdateFieldsIds.indexOf(id);
          if (indexOfIdInRequestsList !== -1) this.requestedToUpdateFieldsIds.splice(indexOfIdInRequestsList, 1);
        });
    }
  };

  promptCertificationStatusChange = itemWasUnchecked => {
    const { currentYearInfo } = this.state;
    const { user } = this.props;
    const uncheckedItemsCount = currentYearInfo.itemValues.filter(
      ({ isChecked, isApplicable }) => !isChecked && isApplicable
    ).length;

    const isOilRelated = getIsOilManufacturer(user) || getIsOilBrander(user);

    if (!(isOilRelated || uncheckedItemsCount || currentYearInfo.isCertified || user.getActiveRole() === ROLES.COMPONENT_MANUFACTURER.serverName)) {
      this.modalHeader = 'Confirmation';
      this.modalBody = 'All checklist items are checked. Would you like to update Certification status to Certified?';
      this.modalConfirmAction = this.toggleCertificationStatus;
      this.setState({ isModalOpen: true });
    } else if (uncheckedItemsCount === 1 && itemWasUnchecked && currentYearInfo.isCertified) {
      this.modalHeader = 'Confirmation';
      this.modalBody =
        'One of checklist items has been unchecked. Would you like to update Certification status to Certification Pending?';
      this.modalConfirmAction = this.toggleCertificationStatus;
      this.setState({ isModalOpen: true });
    }
  };

  toggleCertificationStatus = () => {
    let { currentYearInfo } = this.state;

    currentYearInfo.isCertified = !currentYearInfo.isCertified;
    this.requestedToUpdateFieldsIds.push('modal');

    this.setState({ currentYearInfo }, () => {
      this.saveChecklistInfo()
        .catch(() => {
          this.setState(prevState => {
            return { currentYearInfo: !prevState.currentYearInfo };
          });
        })
        .finally(() => {
          const indexOfIdInRequestsList = this.requestedToUpdateFieldsIds.indexOf('modal');
          if (indexOfIdInRequestsList !== -1) this.requestedToUpdateFieldsIds.splice(indexOfIdInRequestsList, 1);
          this.setState({ isModalOpen: false });
        });
    });
  };

  eSignAgreement = () => {
    const { user } = this.props;
    const { isPWCAgreement, currentYearInfo } = this.state;

    switch (user.getActiveRole()) {
      case ROLES.BOAT_MANUFACTURER.serverName:
        this.modalHeader = 'Confirmation';
        this.modalBody = 'Are you sure you want to generate and e-sign the annual agreement?';
        this.modalConfirmAction = () => {
          this.setState({ modalRequestInProcess: true });
          http
            .get(manufacturerAPI.eSignBoatCertAgreement(currentYearInfo.year, isPWCAgreement))
            .then(() => {
              NotificationManager.success(
                1,
                'Certification Agreement has been successfully generated and sent for e-signature to DocuSign.'
              );
            })
            .finally(this.closeModal);
        };
        this.setState({ isModalOpen: true });
        break;
      case ROLES.COMPONENT_MANUFACTURER.serverName:
        this.modalHeader = 'Confirmation';
        this.modalBody = 'Are you sure you want to generate and e-sign the annual agreement?';
        this.modalConfirmAction = () => {
          this.setState({ modalRequestInProcess: true });
          http
            .get(manufacturerAPI.eSignComponentCertAgreement(currentYearInfo.year))
            .then(() => {
              NotificationManager.success(
                1,
                'Certification Agreement has been successfully generated and sent for e-signature to DocuSign.'
              );
            })
            .finally(this.closeModal);
        };
        this.setState({ isModalOpen: true });
        break;
      case ROLES.TRAILER_MANUFACTURER.serverName:
        this.modalHeader = 'Confirmation';
        this.modalBody = 'Are you sure you want to generate and e-sign the annual agreement?';
        this.modalConfirmAction = () => {
          this.setState({ modalRequestInProcess: true });
          http
            .get(manufacturerAPI.eSignTrailerCertAgreement(currentYearInfo.year))
            .then(() => {
              NotificationManager.success(
                1,
                'Certification Agreement has been successfully generated and sent for e-signature to DocuSign.'
              );
            })
            .finally(this.closeModal);
        };
        this.setState({ isModalOpen: true });
        break;
      default:
        break;
    }
  };

  toggleIsPWCFlag = () => {
    this.setState(prevState => ({ isPWCAgreement: !prevState.isPWCAgreement }));
  };

  addBrand = () => {
    this.modalHeader = 'Add Brand';
    this.modalBody = () => {
      return (
        <BrandDetailsModal handleInputChange={this.handleBrandDetailsChange} brandDetails={this.state.brandDetails} />
      );
    };

    this.modalConfirmAction = () => {
      this.handleBrandSave();
    };
    this.setState({ isModalOpen: true, brandDetails: { errors: {} } });
  };

  editBrand = brandDetails => () => {
    this.modalHeader = 'Edit Brand';
    this.modalBody = () => {
      return (
        <BrandDetailsModal handleInputChange={this.handleBrandDetailsChange} brandDetails={this.state.brandDetails} />
      );
    };

    this.modalConfirmAction = () => {
      this.handleBrandSave();
    };

    this.setState({ isModalOpen: true, brandDetails: { ...brandDetails, errors: {} } });
  };

  handleBrandDetailsChange = fieldName => input => {
    let { brandDetails } = this.state;

    brandDetails[fieldName] = input.value || input;

    this.setState({ brandDetails });
  };

  handleBrandSave = () => {
    const { user } = this.props;
    const { brandDetails } = this.state;

    let brandWebAddress = brandDetails.web;
    if (!brandWebAddress.match(/https?:\/\//)) {
      brandWebAddress = 'http://' + brandWebAddress;
    }

    http
      .post(manufacturerAPI.addBrand, {
        id: brandDetails.id,
        boatType: brandDetails.boatType,
        web: brandWebAddress,
        name: brandDetails.name,
        mnfCode: user.getManufacturerCode(),
      })
      .then(({ data: newBrandEntry }) => {
        let { brandsList } = this.state;

        if (brandDetails.id) {
          const brandEntryIndex = brandsList.findIndex(brand => brand.id === newBrandEntry.id);
          brandsList.splice(brandEntryIndex, 1, newBrandEntry);
        } else {
          brandsList.push(newBrandEntry);
        }
        this.setState({ brandsList });
      })
      .finally(() => {
        this.setState({ isModalOpen: false });
      });
  };

  deleteBrand = id => () => {
    http.delete(manufacturerAPI.deleteBrand(id)).then(() => {
      let { brandsList } = this.state;
      const brandEntryIndex = brandsList.findIndex(brand => brand.id === id);
      brandsList.splice(brandEntryIndex, 1);
      this.setState({ brandsList, isModalOpen: false });
    });
  };

  render() {
    const {
      isModalOpen,
      currentYearInfo,
      modalRequestInProcess,
      isPWCAgreement,
      currentYear,
      year,
      brandsList,
      yearInfoFetchFailed,
    } = this.state;

    const { user } = this.props;

    const agreementStatusEntry =
      this._showAnnualCertificationAgreement() && currentYearInfo
        ? currentYearInfo.itemValues.find(entry => entry.statusType === 'AGREEMENT')
        : '';
    const agreementIsSigned = agreementStatusEntry && agreementStatusEntry.isChecked;

    return (
      <React.Fragment>
        <section className='scene__scene-wrapper'>
          <section className='scene__content-wrapper'>
            <SummaryHeader
              manufacturerName={user.getManufacturerName()}
              manufacturerStatus={user.getManufacturerStatus()}
              selectedManufacturer={user.selectedManufacturer}
              isBoatManufacturer={getIsBoatManufacturer(user)}
            />
            <Contacts user={user} />
            {getIsBoatManufacturer(user) && (
              <BrandsList
                list={brandsList}
                isInternalUser={this.isInternalUser}
                isInspector={user.isInspector()}
                addBrand={this.addBrand}
                editBrand={this.editBrand}
                deleteBrand={this.deleteBrand}
              />
            )}
            {prop('hasCertification', currentYearInfo) && (
              <SummaryCertification
                user={user}
                currentYearInfo={currentYearInfo}
                toggleCertificationStatus={this.toggleCertificationStatus}
              />
            )}
            <SummaryGeneralInfo
              user={user}
              currentYearInfo={currentYearInfo}
              currentYear={currentYear}
              year={year}
              handleYearSelect={this.handleYearSelect}
              handleRoleChange={this.handleRoleChange}
            />

            {yearInfoFetchFailed ? (
              <section className='scene_section _ai_center _jc_center'>
                <header>Something went wrong. Please try again later.</header>
              </section>
            ) : currentYearInfo ? (
              <React.Fragment>
                {this._showAnnualCertificationAgreement() && (
                  <section className='scene_section  _padding_top_15 _padding_bottom_20'>
                    <header className='_margin_bottom_16'>Annual Certification Agreement</header>
                    {getIsBoatManufacturer(user) && (
                      <Slider
                        state={!isPWCAgreement}
                        mainWrapperClassName='green _margin_top_30 _margin_right_150'
                        onLabel='Boat / Yacht'
                        offLabel='PWC'
                        disabled={agreementIsSigned && !this.isInternalUser}
                        toggleAction={this.toggleIsPWCFlag}
                      />
                    )}

                    <div>
                      <div className='_font_size_12 _font_weight_100 _color_light_gray _margin_bottom_10'>
                        E-Sign Annual Agreement with NMMA
                      </div>
                      <Button
                        label='Sign'
                        type={buttonType.primary}
                        color={buttonColor.green}
                        buttonClassName='_height_40 _width_160'
                        disabled={agreementIsSigned && !this.isInternalUser}
                        onClick={this.eSignAgreement}
                      />
                    </div>
                  </section>
                )}

                <SummaryCheckList
                  user={user}
                  isInternalUser={this.isInternalUser}
                  checklist={currentYearInfo.itemValues}
                  chosenYear={year}
                  onCheckListDateChange={this.onCheckListDateChange}
                  onCheckListStatusChange={this.onCheckListStatusChange}
                  onCheckListAutomationChange={this.onCheckListAutomationChange}
                  onCheckListApplicableChange={this.onCheckListApplicableChange}
                  documentTypeForListLink={CLICKABLE_LIST_ITEM_DOCUMENT_TYPE}
                />
                {(this.isInternalUser || (user.isInspector() && currentYearInfo.notes)) && (
                  <section className='scene_section notesScene__history-section _padding_top_15 _padding_bottom_20'>
                    <header>Notes</header>
                    <Notes
                      text={currentYearInfo.notes || ''}
                      handleInputChange={this.handleNotesChange}
                      disabled={!this.isInternalUser}
                    />

                    <h4>History</h4>
                    <div className='history-section'>
                      {currentYearInfo.notesHistory.map(
                        ({ year, notes }) => (
                          <div className='history' key={year}>
                            <h5>{`${year}`}</h5>
                            <div className='notes' dangerouslySetInnerHTML={{__html: notes}}>

                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                )}
                {this.isInternalUser && (
                  <section className='scene_section detailsScene__change-status-section  _padding_top_15 _padding_bottom_20'>
                    <header>Change Manufacturer Status</header>
                    <Button
                      label={user.getManufacturerStatus() ? 'Deactivate' : 'Activate'}
                      type={buttonType.primary}
                      color={user.getManufacturerStatus() ? buttonColor.red : buttonColor.green}
                      buttonClassName='_height_40 _width_160'
                      onClick={this.changeManufacturerStatus}
                    />
                  </section>
                )}
              </React.Fragment>
            ) : (
              <section className='scene_section _ai_center _jc_center'>
                <div className='__spinner _width_90 _height_90' />
              </section>
            )}
          </section>
        </section>

        <Modal isOpen={isModalOpen}>
          <header className='ect-modal-header'>{this.modalHeader}</header>
          <div className='ect-modal-body'>
            {typeof this.modalBody === 'function' ? this.modalBody() : this.modalBody}
          </div>
          <footer className='ect-modal-footer'>
            <Button
              label='No'
              type={buttonType.secondary}
              color={buttonColor.gray}
              buttonClassName='_height_40 _width_160'
              onClick={this.closeModal}
            />
            <Button
              label='Yes'
              buttonClassName='_height_40 _width_160'
              onClick={this.modalConfirmAction}
              disabledWithSpinner={modalRequestInProcess}
            />
          </footer>
        </Modal>

      </React.Fragment>


    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/SummaryScene/index.jsx